import { sentryInit, type SentryInitOptions } from './sentry-init';
import { setSentryDeliveryEnabled } from './sentry-delivery-gate';

/**
 * The part of a LaunchDarkly browser client this gate uses.
 *
 * Declared structurally instead of importing `LDClient`, so this package does
 * not take a dependency on `launchdarkly-js-client-sdk` — `@pypestream/utils`
 * is consumed as source and bundled by each app, and a real import would put
 * the whole SDK into every bundle that touches any util. The real client
 * satisfies this shape; so does a hand-written stub in a test.
 */
export interface SentryGateClient {
  variation(key: string, defaultValue?: unknown): unknown;
  waitForInitialization(timeout?: number): Promise<unknown>;
  on(key: string, handler: (current: unknown) => void): void;
}

/**
 * How long a first visit waits for LaunchDarkly before failing OPEN.
 *
 * SECONDS — that is the unit `waitForInitialization` takes
 * (`launchdarkly-js-sdk-common/typings.d.ts`: "The amount of time, in seconds").
 *
 * Deliberately far below the budget the feature-flag invoke allows itself
 * (30 s in pro-studio's `app.xstate.ts`; manager calls `waitForInitialization()`
 * with no argument at all). That budget is generous because everything there
 * fails CLOSED on expiry, so a tight cutoff would disable features for a merely
 * slow connection. Here expiry means the opposite — we start reporting — so the
 * cost of waiting is a window in which errors are not captured at all, and the
 * cost of expiring early is some events from a deploy that may not have wanted
 * them.
 */
export const SENTRY_GATE_TIMEOUT_SECONDS = 2;

export interface SentryGateOptions {
  client: SentryGateClient;
  /** The LaunchDarkly flag that decides, e.g. `ps-sentry-enabled`. */
  flagKey: string;
  timeoutSeconds?: number;
}

/**
 * Reads the flag off a client that has already initialised.
 *
 * Returns `undefined` — meaning "no answer", NOT "off" — when the client holds
 * nothing for this key, which is why every caller below supplies its own
 * fallback rather than treating absence as a decision.
 *
 * A non-boolean value reads as no answer too: the flag is declared boolean in
 * LaunchDarkly, so a string or a number means the key was reused for something
 * else, and guessing at its truthiness is how a kill switch gets thrown by
 * accident.
 */
const readFlag = (
  client: SentryGateClient,
  flagKey: string
): boolean | undefined => {
  try {
    const value = client.variation(flagKey, undefined);

    return typeof value === 'boolean' ? value : undefined;
  } catch {
    // `variation` on a client that has not started can throw rather than
    // return the fallback, and that is not an answer either.
    return undefined;
  }
};

/**
 * Keeps the running client in step with the flag.
 *
 * Subscribed even when the gate opened by FAILING OPEN, and that is the case it
 * exists for: when LaunchDarkly answers after the timeout, the first value it
 * delivers arrives as a change, so a deploy that was not meant to report stops
 * on its own instead of reporting for the life of the page.
 */
const followFlag = (client: SentryGateClient, flagKey: string): void => {
  try {
    client.on(`change:${flagKey}`, (current) => {
      setSentryDeliveryEnabled(current !== false);
    });
  } catch {
    // A client that cannot take listeners still reports; it just cannot be
    // switched off until the page reloads. Losing the switch is not a reason
    // to lose the error reporting it guards.
  }
};

/**
 * Starts Sentry if — and only if — LaunchDarkly says this deploy reports.
 *
 * Replaces the list of environment names this package used to hold. The deploy
 * still names itself through `options.environment`, but that value is only a
 * TAG on the event now; it decides nothing.
 *
 * Three properties worth keeping when this changes:
 *
 * 1. It never blocks the caller. The decision always waits for LaunchDarkly,
 *    but it waits on its OWN promise rather than handing the caller one to
 *    await — rendering the app must not wait on a flag service. The cost is
 *    stated rather than hidden: until that answer lands there is no client, so
 *    an error thrown in the first moments of the page is not captured. An
 *    earlier revision tried to close that window with `bootstrap: 'localStorage'`
 *    and a synchronous read; that does not work, because the SDK loads a
 *    bootstrapped cache through `persistentFlagStore.loadFlags().then(...)` —
 *    only the `LDFlagSet` form of `bootstrap` is synchronous, which is what the
 *    typings mean by "in the latter case".
 * 2. It fails OPEN. LaunchDarkly being unreachable must not blind production,
 *    and `reportError(err, { source: 'LD-initialization failed' })` in each
 *    app's `app.xstate.ts` is only deliverable if Sentry survives LD being
 *    down — fail-closed would make the outage report unreachable by
 *    construction.
 * 3. Turning the flag off stops the stream in tabs that are already open, via
 *    the `change` subscription. The inverse does not hold: turning it back on
 *    cannot create a client that was never initialised, so it takes effect on
 *    the next load. That asymmetry is why the subscription is only placed when
 *    the gate opened — on a client that was never created there is nothing for
 *    a later `true` to switch on.
 */
export const initSentryWithLaunchDarklyGate = (
  dsn: string,
  options: Omit<SentryInitOptions, 'enabled' | 'disabledReason'>,
  {
    client,
    flagKey,
    timeoutSeconds = SENTRY_GATE_TIMEOUT_SECONDS,
  }: SentryGateOptions
): void => {
  const start = (enabled: boolean, source: string): void => {
    sentryInit(dsn, {
      ...options,
      enabled,
      disabledReason: `the LaunchDarkly flag "${flagKey}" is off (${source})`,
    });

    if (enabled) followFlag(client, flagKey);
  };

  try {
    client
      .waitForInitialization(timeoutSeconds)
      .then(() => {
        // Fail open here too: the client is up but carries no value for this
        // key — an archived or mistyped flag — and that is a configuration gap,
        // not an instruction to stop reporting.
        start(readFlag(client, flagKey) ?? true, 'LaunchDarkly');
      })
      .catch(() => {
        start(true, 'LaunchDarkly unreachable — failed open');
      });
  } catch {
    // `waitForInitialization` throwing synchronously would otherwise escape
    // into the caller's `DOMContentLoaded` handler and skip the `root.render`
    // below it — a white page instead of an app with no error reporting. The
    // other two client calls are guarded for the same reason.
    start(true, 'LaunchDarkly client unusable — failed open');
  }
};
